import type { OutgoingIntakeAnswer } from "../../api/types/outgoingIntake";

export type GroupInstance = {
  instanceId: string;
  answers: Record<string, OutgoingIntakeAnswer>;
};

export type HydratedAnswerState = {
  answersById: Record<string, OutgoingIntakeAnswer>;
  groups: Record<string, GroupInstance[]>;
};

export type DraftSnapshot = HydratedAnswerState & {
  savedAtIso: string | null;
  savedAtMs: number | null;
};

export function buildHydratedAnswerState(
  answers?: OutgoingIntakeAnswer[] | null
): HydratedAnswerState {
  const answersById: Record<string, OutgoingIntakeAnswer> = {};
  const groupedAnswers: Record<
    string,
    Record<string, Record<string, OutgoingIntakeAnswer>>
  > = {};

  for (const answer of answers ?? []) {
    if (answer.repeatable_group_id && answer.repeatable_group_instance_id) {
      const groupId = answer.repeatable_group_id;
      const instanceId = answer.repeatable_group_instance_id;
      groupedAnswers[groupId] = groupedAnswers[groupId] ?? {};
      groupedAnswers[groupId][instanceId] =
        groupedAnswers[groupId][instanceId] ?? {};
      groupedAnswers[groupId][instanceId][answer.question_id] = answer;
    } else {
      answersById[answer.question_id] = answer;
    }
  }

  const groups: Record<string, GroupInstance[]> = {};
  for (const [groupId, instances] of Object.entries(groupedAnswers)) {
    groups[groupId] = Object.entries(instances).map(
      ([instanceId, instanceAnswers]) => ({
        instanceId,
        answers: instanceAnswers
      })
    );
  }

  return { answersById, groups };
}

export function toMillis(value: unknown): number | null {
  if (!value) return null;
  try {
    const obj = value as { toDate?: () => Date; seconds?: number };
    let d: Date | null = null;
    if (typeof obj?.toDate === "function") d = obj.toDate();
    else if (typeof obj?.seconds === "number") d = new Date(obj.seconds * 1000);
    else if (typeof value === "string") {
      const parsed = new Date(value);
      if (!Number.isNaN(parsed.getTime())) d = parsed;
    } else if (typeof value === "number") d = new Date(value);
    else if (value instanceof Date) d = value;
    if (!d || Number.isNaN(d.getTime())) return null;
    return d.getTime();
  } catch {
    return null;
  }
}

export function normalizeSavedAtIso(value: unknown): string | null {
  const ms = toMillis(value);
  if (ms !== null) return new Date(ms).toISOString();
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function hasHydratedAnswerState(state: HydratedAnswerState): boolean {
  return (
    Object.keys(state.answersById).length > 0 ||
    Object.keys(state.groups).length > 0
  );
}

export function createServerDraftSnapshot(
  answers?: OutgoingIntakeAnswer[] | null,
  savedAt?: unknown
): DraftSnapshot | null {
  const state = buildHydratedAnswerState(answers);
  const savedAtIso = normalizeSavedAtIso(savedAt);
  if (!hasHydratedAnswerState(state) && !savedAtIso) return null;
  return {
    ...state,
    savedAtIso,
    savedAtMs: toMillis(savedAt)
  };
}

export function readLocalDraftSnapshot(
  raw: string | null
): DraftSnapshot | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as {
      saved_at?: string;
      answersById?: Record<string, OutgoingIntakeAnswer>;
      groups?: Record<string, GroupInstance[]>;
    };
    const answersById =
      parsed.answersById && typeof parsed.answersById === "object"
        ? parsed.answersById
        : {};
    const groups =
      parsed.groups && typeof parsed.groups === "object" ? parsed.groups : {};
    const savedAtIso = normalizeSavedAtIso(parsed.saved_at);

    if (
      Object.keys(answersById).length === 0 &&
      Object.keys(groups).length === 0 &&
      !savedAtIso
    ) {
      return null;
    }

    return {
      answersById,
      groups,
      savedAtIso,
      savedAtMs: toMillis(parsed.saved_at)
    };
  } catch {
    return null;
  }
}

/**
 * Overlay one set of repeatable-group instances on top of another, merging
 * answers per instance. Base instances keep their order; instances only present
 * in the overlay are appended. Used to restore autosaved edits on top of a
 * hydrated baseline without losing baseline answers the overlay omits.
 */
export function mergeGroupInstances(
  base: Record<string, GroupInstance[]>,
  overlay: Record<string, GroupInstance[]>
): Record<string, GroupInstance[]> {
  const out: Record<string, GroupInstance[]> = {};
  const groupIds = new Set([...Object.keys(base), ...Object.keys(overlay)]);
  for (const groupId of groupIds) {
    const remaining = new Map(
      (overlay[groupId] ?? []).map((item) => [item.instanceId, item])
    );
    const merged = (base[groupId] ?? []).map((item) => {
      const overlayItem = remaining.get(item.instanceId);
      if (!overlayItem) return item;
      remaining.delete(item.instanceId);
      return {
        instanceId: item.instanceId,
        answers: { ...item.answers, ...(overlayItem.answers ?? {}) }
      };
    });
    for (const overlayItem of remaining.values()) merged.push(overlayItem);
    out[groupId] = merged;
  }
  return out;
}

export function pickPreferredDraft(
  serverDraft: DraftSnapshot | null,
  localDraft: DraftSnapshot | null
): DraftSnapshot | null {
  if (!serverDraft) return localDraft;
  if (!localDraft) return serverDraft;

  if (serverDraft.savedAtMs === null && localDraft.savedAtMs === null) {
    return serverDraft;
  }
  if (serverDraft.savedAtMs === null) return localDraft;
  if (localDraft.savedAtMs === null) return serverDraft;

  return localDraft.savedAtMs > serverDraft.savedAtMs
    ? localDraft
    : serverDraft;
}
